import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Funnel analytics. Screens call `track()`; where events go is the sink's business.
 * Wave 1 ships a no-op sink. Later sinks: GA4 + ad pixels (consent-gated) and the Intake
 * API's `enquiry_events` (architecture.md §3) — compose them with `fanOut`.
 */
export type AnalyticsEvent =
  { name: 'screen_view'; screen: string; path: string } | { name: string; [prop: string]: unknown };

export interface AnalyticsSink {
  track(event: AnalyticsEvent): void;
}

export const noopSink: AnalyticsSink = { track: () => {} };

/** Dev aid: logs every event to the console. */
export const consoleSink: AnalyticsSink = {
  track: (event) => console.debug('[analytics]', event),
};

export const fanOut = (...sinks: AnalyticsSink[]): AnalyticsSink => ({
  track: (event) =>
    sinks.forEach((s) => {
      try {
        s.track(event);
      } catch {
        /* one broken sink must never break the funnel */
      }
    }),
});

const AnalyticsContext = createContext<AnalyticsSink>(noopSink);

export function AnalyticsProvider({
  sink = noopSink,
  children,
}: {
  sink?: AnalyticsSink;
  children: ReactNode;
}) {
  return <AnalyticsContext.Provider value={sink}>{children}</AnalyticsContext.Provider>;
}

/** `const track = useTrack(); track('s1_connection_added', { count: 2 })` */
export function useTrack() {
  const sink = useContext(AnalyticsContext);
  return useMemo(
    () =>
      (name: string, props: Record<string, unknown> = {}) =>
        sink.track({ ...props, name }),
    [sink],
  );
}

export function useAnalyticsSink(): AnalyticsSink {
  return useContext(AnalyticsContext);
}
